'use client';

import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkBreaks from 'remark-breaks';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import CodeBlock from './CodeBlock';
import { MermaidDiagram } from '@/components/exp-hub/MermaidDiagram';

interface MarkdownProps {
 /** Server-rendered HTML (preferred fast path on the public page). */
 html?: string;
 /** Raw MDX — used when html is not available (legacy projects, admin preview). */
 mdx?: string;
 /** Open external links in new tab. */
 openLinksInNewTab?: boolean;
}

/**
 * Markdown — shared renderer for project case-study content.
 *
 * Used by BOTH the admin live preview AND the public detail
 * page, so what the admin sees is exactly what the visitor
 * gets. The plugin chain is identical to the backend's
 * `renderProjectMarkdown()` pipeline:
 *
 * remark-gfm + remark-breaks + rehype-raw + rehype-pretty-code
 *
 * • remark-gfm: tables, strikethrough, task-lists
 * • remark-breaks: hard wraps become <br> (matches
 * admin-side expectation; without this, paragraphs pasted
 * with manual line breaks collapse to one line)
 * • rehype-raw: allow the callout preprocessor's <aside>
 * blocks to pass through
 *
 * Fenced code blocks are NOT highlighted by a rehype plugin.
 * react-markdown runs its rehype chain via `runSync()`, which
 * throws "runSync finished async. Use run instead" the moment a
 * plugin transforms asynchronously — and Shiki (rehype-pretty-code)
 * is async. So instead we render each ```fence``` through the
 * <CodeBlock /> component, which performs the Shiki highlight in a
 * React effect (async-safe) and gives us the exact same theme.
 *
 * Two render paths:
 * 1. If `html` is provided, render directly via
 * dangerouslySetInnerHTML (after DOMPurify sanitization).
 * This is the preferred path: backend already rendered
 * once on save and cached the result.
 * 2. Otherwise, render `mdx` client-side with the same
 * plugin chain so unsaved drafts and legacy projects
 * still display correctly.
 *
 * NOTE: The HTML path relies on the backend to have run
 * `rehype-pretty-code` (or equivalent) for fenced code.
 * Currently the backend uses `rehype-highlight` (hljs), so
 * the public page's HTML path will use hljs classes — the
 * admin preview uses Shiki instead. To unify, we could
 * upgrade the backend to rehype-pretty-code in a follow-up.
 * The CRITICAL shared surface (the <CodeBlock /> component
 * for standalone schema + milestone code review) already
 * uses Shiki on both paths.
 */
export default function Markdown({
 html,
 mdx,
 openLinksInNewTab = true,
}: MarkdownProps) {
 const [safeHtml, setSafeHtml] = useState<string>('');
 const bodyRef = useRef<HTMLDivElement | null>(null);

 // Sanitize the server-rendered HTML once on the client.
 // The backend already ran rehype-sanitize, but defence in
 // depth is cheap and protects against any future pipeline
 // change that drops sanitisation.
 useEffect(() => {
 let cancelled = false;
 if (!html) { setSafeHtml(''); return; }
 (async () => {
 const mod = await import('isomorphic-dompurify');
 const DOMPurify = mod.default;
 const cleaned = DOMPurify.sanitize(html, {
 ALLOWED_TAGS: [
 'p', 'br', 'strong', 'em', 'u', 's', 'del', 'ins', 'mark',
 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
 'ul', 'ol', 'li',
 'blockquote', 'q', 'cite',
 'code', 'pre', 'kbd', 'samp', 'var',
 'a', 'img', 'figure', 'figcaption',
 'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
 'hr', 'details', 'summary', 'aside',
 'span', 'div', 'figure',
 ],
 ALLOWED_ATTR: [
 'href', 'title', 'target', 'rel',
 'src', 'alt', 'width', 'height',
 'class', 'id', 'className',
 'style', 'data-callout', 'data-title', 'data-language',
 'data-theme', 'data-line', 'data-highlighted-line',
 'tabindex', 'role', 'aria-hidden',
 ],
 });
 // Bọc mỗi bảng trong khung cuộn ngang riêng (.table-scroll) — xem globals.css.
 if (!cancelled) setSafeHtml(cleaned.replace(/<table\b/g, '<div class="table-scroll"><table').replace(/<\/table>/g, '</table></div>'));
 })();
 return () => { cancelled = true; };
 }, [html]);

 // Draw ```mermaid fences as SVG once the sanitised HTML is in the DOM.
 // Mutating in place (rather than portalling) keeps `.case-study-body`'s
 // children exactly as the CSS expects — see mermaidRuntime for why.
 useEffect(() => {
 if (!safeHtml) return;
 const root = bodyRef.current;
 if (!root) return;
 let cancelled = false;
 (async () => {
 const { renderMermaidBlocks } = await import('./mermaidRuntime');
 if (!cancelled && bodyRef.current) await renderMermaidBlocks(bodyRef.current);
 })().catch(() => { /* diagram stays a code block */ });
 return () => { cancelled = true; };
 }, [safeHtml]);

 if (html && safeHtml) {
 return (
 <div
 ref={bodyRef}
 className="case-study-body"
 dangerouslySetInnerHTML={{ __html: safeHtml }}
 />
 );
 }

 if (!mdx) return null;

 return (
 <div className="case-study-body">
 <ReactMarkdown
 remarkPlugins={[remarkGfm, remarkBreaks]}
 rehypePlugins={[
 rehypeRaw,
 rehypeSanitize,
 ]}
 components={{
 // External links open in new tab with noopener.
 a: ({ node, ...props }) => {
 const href = (props.href ?? '') as string;
 const isExternal = /^https?:\/\//i.test(href);
 if (isExternal && openLinksInNewTab) {
 return <a {...props} target="_blank" rel="noopener noreferrer" />;
 }
 return <a {...props} />;
 },
 // Inline code in our design system — neon-violet pill.
 // Fenced code blocks (className `language-xxx`) are
 // rendered by <CodeBlock />, which highlights with Shiki
 // asynchronously inside a React effect — safe under
 // react-markdown's synchronous rehype runner.
 code: ({ className, children, ...props }) => {
 const match = /language-(\w+)/.exec(className ?? '');
 if (match) {
 const code = String(children ?? '').replace(/\n$/, '');
 // Diagrams render as diagrams in the admin preview too, so the
 // editor sees exactly what the visitor will get.
 if (match[1] === 'mermaid') return <MermaidDiagram chart={code} className="my-6" />;
 return <CodeBlock code={code} language={match[1]} />;
 }
    // ⚠️ FENCE KHÔNG GẮN TÊN NGÔN NGỮ VẪN LÀ KHỐI, KHÔNG PHẢI CODE NỘI DÒNG.
    // react-markdown chỉ đặt `language-xxx` khi fence CÓ nhãn. Fence trơn
    // (```) rơi xuống nhánh này và được vẽ bằng <code> — không <pre>, không
    // `white-space: pre` — nên MỌI xuống dòng biến thành dấu cách. Đo thật
    // trên trợ giảng LAB211: một cây thư mục 5 dòng bị ép thành đoạn văn chạy
    // dài, không đọc nổi. Với môn chấm console TỪNG KÝ TỰ thì đây là kiểu
    // hỏng đắt nhất — nó nuốt đúng phần "màn hình mong đợi".
    // Dấu hiệu chắc chắn và rẻ nhất: nội dung có xuống dòng thì đó là KHỐI.
    const noiDung = String(children ?? '');
    if (noiDung.includes('\n')) {
      return <CodeBlock code={noiDung.replace(/\n$/, '')} language="text" />;
    }
 return (
 <code className="px-1.5 py-0.5 rounded text-[0.9em] bg-neon-violet/10 text-neon-violet border border-neon-violet/20 font-mono" {...props}>
 {children}
 </code>
 );
 },
 // <CodeBlock /> renders its own <pre> chrome, so unwrap
 // react-markdown's surrounding <pre> to avoid invalid
 // <pre><div> nesting. Inline code never reaches here.
 pre: ({ children }) => <>{children}</>,
 // Bảng rộng cuộn ngang trong khung riêng thay vì bóp cột — xem .table-scroll trong globals.css.
 table: ({ node, ...props }) => <div className="table-scroll"><table {...props} /></div>,
 h1: ({ id, children, ...props }) => <h1 id={id} {...props}>{children}</h1>,
 h2: ({ id, children, ...props }) => <h2 id={id} {...props}>{children}</h2>,
 h3: ({ id, children, ...props }) => <h3 id={id} {...props}>{children}</h3>,
 h4: ({ id, children, ...props }) => <h4 id={id} {...props}>{children}</h4>,
 }}
 >
 {mdx}
 </ReactMarkdown>
 </div>
 );
}
